import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { heroImageBackgroundPosition, normalizeHeroImageFocal } from '../heroImageFocal';

function isInternalPath(path) {
  const p = String(path || '').trim();
  return p.startsWith('/') && !p.startsWith('//');
}

function stopNav(e) {
  e.preventDefault();
  e.stopPropagation();
}

/**
 * Fade carousel for home hero — controls sit above overlays (fixes blocked next/prev clicks).
 * @param {'both'|'backdrop'|'foreground'} layers — premium: split atmosphere vs portrait
 */
export default function HomeHeroCarousel({
  slides = [],
  autoplaySeconds = 6,
  badge = '',
  className = '',
  imageFit = 'contain',
  immersive = false,
  splitChrome = false,
  columnStage = false,
  premiumStage = false,
  layers = 'both',
  controlledIndex,
  onIndexChange,
  onSlideReady,
}) {
  const [index, setIndex] = useState(0);
  const [readyKeys, setReadyKeys] = useState(() => new Set());
  const count = slides.length;
  const timerRef = useRef(null);
  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isControlled = controlledIndex !== undefined;
  const activeIndex = isControlled ? controlledIndex : index;

  const setActiveIndex = useCallback(
    (next) => {
      const normalized = ((next % count) + count) % count;
      if (!isControlled) setIndex(normalized);
      onIndexChange?.(normalized);
    },
    [count, isControlled, onIndexChange]
  );

  const goTo = useCallback(
    (targetIndex) => {
      if (count < 2) return;
      setActiveIndex(targetIndex);
    },
    [count, setActiveIndex]
  );

  const goNext = useCallback(() => {
    if (count < 2) return;
    setActiveIndex(activeIndex + 1);
  }, [count, activeIndex, setActiveIndex]);

  const goPrev = useCallback(() => {
    if (count < 2) return;
    setActiveIndex(activeIndex - 1 + count);
  }, [count, activeIndex, setActiveIndex]);

  const markReady = useCallback(
    (key) => {
      setReadyKeys((prev) => {
        if (prev.has(key)) return prev;
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      onSlideReady?.(true);
    },
    [onSlideReady]
  );

  const slideKey = slides.map((s) => `${s.id}:${s.src}`).join('|');

  useEffect(() => {
    if (!isControlled) {
      setIndex(0);
    }
    setReadyKeys(new Set());
    onSlideReady?.(false);
  }, [slideKey, onSlideReady, isControlled]);

  useEffect(() => {
    if (isControlled || layers === 'backdrop') return undefined;
    if (count < 2 || reducedMotion || autoplaySeconds <= 0) return undefined;
    timerRef.current = window.setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, autoplaySeconds * 1000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [count, autoplaySeconds, reducedMotion, isControlled, layers]);

  const pauseAutoplay = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  if (!count) {
    return <div className={`home-hero__media-placeholder${className ? ` ${className}` : ''}`} aria-hidden />;
  }

  const anyReady = readyKeys.size > 0;
  const active = slides[activeIndex] || slides[0];
  const link = String(active?.link || '/shop').trim() || '/shop';
  const isCinematic = imageFit === 'cinematic' || premiumStage;
  const isCover = imageFit === 'cover';
  const fitClass = isCover
    ? 'home-hero__media-img--cover'
    : isCinematic
      ? ''
      : 'home-hero__media-img--contain';

  const showBackdrop = layers === 'both' || layers === 'backdrop';
  const showForeground = layers === 'both' || layers === 'foreground';
  const showControls = layers !== 'backdrop' && count > 1;

  /** Backdrop uses a heavy blurred full-bleed image — only mount the active slide. */
  const slideIndexes =
    layers === 'backdrop'
      ? [activeIndex]
      : slides.map((_, i) => i);

  const controlsUi = showControls ? (
    <div className="home-hero__carousel-ui" aria-label="Hero slideshow controls">
      <button
        type="button"
        className="home-hero__carousel-btn home-hero__carousel-btn--prev"
        aria-label="Previous photo"
        onClick={(e) => {
          stopNav(e);
          pauseAutoplay();
          goPrev();
        }}
      >
        <span aria-hidden>‹</span>
      </button>
      <button
        type="button"
        className="home-hero__carousel-btn home-hero__carousel-btn--next"
        aria-label="Next photo"
        onClick={(e) => {
          stopNav(e);
          pauseAutoplay();
          goNext();
        }}
      >
        <span aria-hidden>›</span>
      </button>
      <div className="home-hero__carousel-dots" role="tablist" aria-label="Choose photo">
        {slides.map((slide, i) => (
          <button
            key={`dot-${slide.id}`}
            type="button"
            role="tab"
            aria-selected={i === activeIndex}
            aria-label={`Photo ${i + 1} of ${count}`}
            className={`home-hero__carousel-dot${i === activeIndex ? ' is-active' : ''}`}
            onClick={(e) => {
              stopNav(e);
              pauseAutoplay();
              goTo(i);
            }}
          />
        ))}
      </div>
      <span className="home-hero__carousel-count" aria-hidden>
        {activeIndex + 1} / {count}
      </span>
    </div>
  ) : null;

  const carouselBody = (
    <div
      className={`home-hero__carousel${anyReady ? ' is-ready' : ''}${immersive ? ' home-hero__carousel--immersive' : ''}${layers === 'backdrop' ? ' home-hero__carousel--backdrop-only' : ''}${layers === 'foreground' ? ' home-hero__carousel--foreground-only' : ''}${className ? ` ${className}` : ''}`}
    >
      <div className="home-hero__carousel-track" aria-live="polite" aria-atomic="true">
        {slideIndexes.map((i) => {
          const slide = slides[i];
          if (!slide) return null;
          const focal = normalizeHeroImageFocal({ x: slide.focal_x, y: slide.focal_y });
          const layoutFocal =
            premiumStage && isCinematic
              ? {
                  x:
                    slide.focal_x != null && String(slide.focal_x).trim() !== ''
                      ? focal.x
                      : 50,
                  y:
                    slide.focal_y != null && String(slide.focal_y).trim() !== ''
                      ? focal.y
                      : 50,
                }
              : columnStage && isCinematic
                ? {
                    x:
                      slide.focal_x != null && String(slide.focal_x).trim() !== ''
                        ? focal.x
                        : 50,
                    y: focal.y,
                  }
                : focal;
          const pos = heroImageBackgroundPosition(layoutFocal);
          const isActive = i === activeIndex;
          return (
            <div
              key={slide.id || slide.src || i}
              className={`home-hero__carousel-slide${isActive ? ' is-active' : ''}${isCinematic ? ' home-hero__carousel-slide--cinematic' : ''}`}
              aria-hidden={!isActive}
            >
              {isCinematic ? (
                <>
                  {showBackdrop ? (
                    <img
                      src={slide.src}
                      alt=""
                      aria-hidden
                      className="home-hero__media-img home-hero__media-img--backdrop"
                      style={{ objectPosition: pos }}
                      decoding="async"
                      loading="eager"
                      fetchPriority="low"
                      width={800}
                      height={1000}
                    />
                  ) : null}
                  {showForeground ? (
                    <img
                      src={slide.src}
                      alt={slide.alt || ''}
                      className="home-hero__media-img home-hero__media-img--foreground"
                      style={{ objectPosition: pos }}
                      decoding={i === activeIndex ? 'async' : 'lazy'}
                      loading={i === activeIndex ? 'eager' : 'lazy'}
                      fetchPriority={i === activeIndex ? 'high' : 'low'}
                      onLoad={() => markReady(slide.id)}
                      onError={() => markReady(slide.id)}
                      width={800}
                      height={1000}
                    />
                  ) : null}
                </>
              ) : (
                <img
                  src={slide.src}
                  alt={slide.alt || ''}
                  className={`home-hero__media-img ${fitClass}`.trim()}
                  style={{ objectPosition: pos }}
                  decoding={i === activeIndex ? 'async' : 'lazy'}
                  loading={i === activeIndex ? 'eager' : 'lazy'}
                  fetchPriority={i === activeIndex ? 'high' : 'low'}
                  onLoad={() => markReady(slide.id)}
                  onError={() => markReady(slide.id)}
                  width={800}
                  height={1000}
                />
              )}
            </div>
          );
        })}
      </div>

      {badge && showForeground ? <span className="home-hero__badge">{badge}</span> : null}

      {splitChrome ? null : controlsUi}
    </div>
  );

  const shopLinkLabel = 'Browse collection';

  const mediaFrame = (
    <div
      className={`home-hero__media-frame${immersive ? ' home-hero__media-frame--immersive' : ''}${columnStage || premiumStage ? ' home-hero__media-frame--column-stage' : ''}${premiumStage && layers === 'foreground' ? ' home-hero__media-frame--premium-fg' : ''}`}
    >
      <div className="home-hero__media">{carouselBody}</div>
      {showForeground && isInternalPath(link) ? (
        <Link
          to={link}
          className="home-hero__media-shop-link"
          aria-label={shopLinkLabel}
          onClick={(e) => e.stopPropagation()}
        >
          {shopLinkLabel}
        </Link>
      ) : showForeground && link.startsWith('http') ? (
        <a
          href={link}
          className="home-hero__media-shop-link"
          aria-label={shopLinkLabel}
          rel="noopener noreferrer"
        >
          {shopLinkLabel}
        </a>
      ) : null}
    </div>
  );

  if (splitChrome) {
    return (
      <>
        <div className="home-hero__immersive-media">{mediaFrame}</div>
        {controlsUi}
      </>
    );
  }

  return mediaFrame;
}
